import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './PlanUsageModal.module.css';
import { homeownerBillingService, proposalComparisonService, userService } from '../../../services/api';

const PlanUsageModal = ({ isOpen, onClose, subscriptionData, forceRefresh }) => {
  const navigate = useNavigate();
  const [usageData, setUsageData] = useState({
    filesUploaded: 0,
    projectComparisons: 0,
    storageUsed: 0,
    totalStorage: 250, // Default 250MB for free/essential
    addonComparisons: 0 // Extra comparisons from add-on purchases
  });
  const [isLoading, setIsLoading] = useState(false);

  // Add-on configuration (matches database values)
  const ADDON_CONFIG = {
    'extra-comparisons': {
      name: 'Extra Comparisons Pack',
      price: 15.00,
      pricePerUnit: 3.00,
      quantity: 5,
      description: 'Add 5 additional project comparisons'
    }
  };

  // Calculate storage limits based on plan
  const getStorageLimits = (planName) => {
    const plan = planName?.toLowerCase();
    switch (plan) {
      case 'complete':
        return 1024; // 1GB
      case 'expert':
        return 5120; // 5GB
      case 'essential':
        return 250; // 250MB
      case 'free':
      default:
        return 250; // 250MB
    }
  };

  // Get comparison limits based on plan (includes addon comparisons)
  const getComparisonLimits = (planName, addonComparisons = 0) => {
    const plan = planName?.toLowerCase();
    let baseLimit;
    switch (plan) {
      case 'essential':
        baseLimit = 5; // 5 comparisons
        break;
      case 'complete':
        baseLimit = 10; // 10 comparisons
        break;
      case 'expert':
        baseLimit = 20; // 20 comparisons
        break;
      case 'free':
      default:
        baseLimit = 2; // 2 comparisons
        break;
    }
    
    // Convert both to numbers to prevent string concatenation
    const numericBaseLimit = Number(baseLimit);
    const numericAddonComparisons = Number(addonComparisons) || 0;
    
    return numericBaseLimit + numericAddonComparisons;
  };

  const getPlanDisplayName = (planName) => {
    if (!planName) return 'Free Tier';
    
    const plan = planName.toLowerCase();
    switch (plan) {
      case 'essential':
        return 'Essential Plan Member';
      case 'complete':
        return 'Complete Plan Member';
      case 'expert':
        return 'Expert Plan Member';
      default:
        return `${planName} Plan Member`;
    }
  };

  useEffect(() => {
    if (isOpen) {
      const totalStorage = getStorageLimits(subscriptionData?.plan_name);
      setUsageData(prev => ({
        ...prev,
        totalStorage
      }));
      // Single fetch when modal opens - remove duplicate calls
      fetchUsageData();
    }
  }, [isOpen, subscriptionData]);

  // Effect to handle force refresh from parent component (only if modal is already open)
  useEffect(() => {
    if (forceRefresh && isOpen) {
      console.log('PlanUsageModal: Force refresh triggered');
      fetchUsageData();
    }
  }, [forceRefresh]);

  // Listen for subscription updates
  useEffect(() => {
    const handleSubscriptionUpdate = () => {
      console.log('PlanUsageModal: Subscription updated, refreshing data');
      if (isOpen) {
        fetchUsageData();
      }
    };

    window.addEventListener('subscriptionUpdated', handleSubscriptionUpdate);
    return () => window.removeEventListener('subscriptionUpdated', handleSubscriptionUpdate);
  }, [isOpen]);

  const fetchUsageData = async () => {
    try {
      // Only show loading if we don't have any existing data (prevents flickering)
      if (!usageData.filesUploaded && !usageData.projectComparisons) {
      setIsLoading(true);
      }
      
      // console.log('=== PlanUsageModal fetchUsageData START ===');
      
      // Calculate isFreeTier within function to get current state
      const currentIsFreeTier = !subscriptionData || !subscriptionData.plan_name;
      
      // Fetch actual usage data from API
      const [userStatsResponse, addonComparisonsResponse] = await Promise.all([
        // Get user stats including file count, storage usage, and total comparisons used
        userService.getUserStats(),
        
        // ALWAYS fetch addon comparisons to ensure fresh data
        homeownerBillingService.getUserAddonComparisons().catch(() => ({ data: { data: { addonComparisons: 0 } } }))
      ]);

      // console.log('PlanUsageModal - RAW userStatsResponse:', userStatsResponse);
      
      const userStats = userStatsResponse.data?.data || {};
      const comparisonsCount = userStats.proposalComparisons || 0; // Use permanent counter from getUserStats
      const addonComparisons = Number(addonComparisonsResponse.data?.data?.addonComparisons) || 0;
      
      // console.log('PlanUsageModal - PROCESSED userStats:', userStats);
      // console.log('PlanUsageModal - PROCESSED comparisonsCount:', comparisonsCount);
      // console.log('PlanUsageModal - PROCESSED addonComparisons:', addonComparisons);
      // console.log('PlanUsageModal - userStats.filesUploaded:', userStats.filesUploaded);
      // console.log('PlanUsageModal - userStats.storageUsedMB:', userStats.storageUsedMB);
      
      const newUsageData = {
        filesUploaded: userStats.filesUploaded || 0,
        projectComparisons: comparisonsCount,
        storageUsed: userStats.storageUsedMB || 0,
        addonComparisons: addonComparisons
      };
      
      // console.log('PlanUsageModal - FINAL newUsageData:', newUsageData);
      
      setUsageData(prev => ({
        ...prev,
        ...newUsageData
      }));
      
      // console.log('=== PlanUsageModal fetchUsageData END ===');
    } catch (error) {
      console.error('Error fetching usage data:', error);
      // Set to 0 on error
      setUsageData(prev => ({
        ...prev,
        filesUploaded: 0,
        projectComparisons: 0,
        storageUsed: 0,
        addonComparisons: 0
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const formatStorage = (sizeInMB) => {
    if (sizeInMB >= 1024) {
      return `${(sizeInMB / 1024).toFixed(1)} GB`;
    }
    return `${sizeInMB} MB`;
  };

  const getStoragePercentage = () => {
    return Math.min((usageData.storageUsed / usageData.totalStorage) * 100, 100);
  };

  const planName = subscriptionData?.plan_name || 'Free';
  const isFreeTier = !subscriptionData || !subscriptionData.plan_name;

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{getPlanDisplayName(planName)}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className={styles.modalBody}>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <i className="fas fa-spinner fa-spin"></i>
              <p>Loading usage data...</p>
            </div>
          ) : (
            <>
              {/* Plan Status */}
              <div className={styles.planStatus}>
                <div className={styles.statusBadge}>
                  <span className={`${styles.badge} ${isFreeTier ? styles.free : styles.premium}`}>
                    {isFreeTier ? 'Free' : planName.toUpperCase()}
                  </span>
                </div>
                {subscriptionData?.status === 'active' && subscriptionData?.current_period_end && (
                  <p className={styles.billingInfo}>
                    Next billing: {new Date(subscriptionData.current_period_end).toLocaleDateString()}
                  </p>
                )}
              </div>

              {/* Usage Statistics */}
              <div className={styles.usageSection}>
                <h3>Usage Overview</h3>
                
                {/* Storage Usage */}
                <div className={styles.usageItem}>
                  <div className={styles.usageHeader}>
                    <div className={styles.usageInfo}>
                      <i className="fas fa-hdd"></i>
                      <span>File Storage</span>
                    </div>
                    <div className={styles.usageValue}>
                      {formatStorage(usageData.storageUsed)} / {formatStorage(usageData.totalStorage)}
                    </div>
                  </div>
                  <div className={styles.progressBar}>
                    <div 
                      className={styles.progressFill}
                      style={{ width: `${getStoragePercentage()}%` }}
                    ></div>
                  </div>
                  <p className={styles.usagePercent}>
                    {getStoragePercentage().toFixed(1)}% used
                  </p>
                </div>

                {/* Files Uploaded */}
                <div className={styles.usageItem}>
                  <div className={styles.usageHeader}>
                    <div className={styles.usageInfo}>
                      <i className="fas fa-file-upload"></i>
                      <span>Files Uploaded</span>
                    </div>
                    <div className={styles.usageValue}>
                      {usageData.filesUploaded} files
                    </div>
                  </div>
                </div>

                {/* Project Comparisons */}
                <div className={styles.usageItem}>
                  <div className={styles.usageHeader}>
                    <div className={styles.usageInfo}>
                      <i className="fas fa-chart-bar"></i>
                      <span>Project Comparisons</span>
                    </div>
                    <div className={styles.usageValue}>
                      {(() => {
                        const limit = getComparisonLimits(planName, usageData.addonComparisons);
                        if (limit === null) {
                          return `${usageData.projectComparisons} / Unlimited`;
                        }
                        return `${usageData.projectComparisons}/${limit} comparisons`;
                      })()}
                    </div>
                  </div>
                </div>
              </div>

                             {/* Plan Limits */}
               <div className={styles.featuresSection}>
                 <h3>Your Plan Limits</h3>
                 <div className={styles.featuresList}>
                   <div className={styles.feature}>
                     <i className="fas fa-check"></i>
                     <span>File Storage: {formatStorage(usageData.totalStorage)}</span>
                   </div>
                   
                   {/* Essential Plan Limits */}
                   {planName?.toLowerCase() === 'essential' && (
                     <div className={styles.feature}>
                       <i className="fas fa-check"></i>
                       <span>
                         Up to {getComparisonLimits('essential', usageData.addonComparisons)} Project Comparisons
                         {usageData.addonComparisons > 0 && (
                           <span className={styles.addonNote}> (includes {usageData.addonComparisons} addon comparisons)</span>
                         )}
                       </span>
                     </div>
                   )}
                   
                   {/* Complete Plan Limits */}
                   {planName?.toLowerCase() === 'complete' && (
                     <div className={styles.feature}>
                       <i className="fas fa-check"></i>
                       <span>
                         Up to {getComparisonLimits('complete', usageData.addonComparisons)} Project Comparisons
                         {usageData.addonComparisons > 0 && (
                           <span className={styles.addonNote}> (includes {usageData.addonComparisons} addon comparisons)</span>
                         )}
                       </span>
                     </div>
                   )}
                   
                   {/* Expert Plan Limits */}
                   {planName?.toLowerCase() === 'expert' && (
                     <div className={styles.feature}>
                       <i className="fas fa-check"></i>
                       <span>
                         Up to {getComparisonLimits('expert', usageData.addonComparisons)} Project Comparisons
                         {usageData.addonComparisons > 0 && (
                           <span className={styles.addonNote}> (includes {usageData.addonComparisons} addon comparisons)</span>
                         )}
                       </span>
                     </div>
                   )}
                   
                   {/* Free Plan Limits */}
                   {isFreeTier && (
                     <div className={styles.feature}>
                       <i className="fas fa-check"></i>
                       <span>Limited to 2 Project Comparisons</span>
                     </div>
                   )}
                 </div>
               </div>

              {/* Extra Comparisons Add-on Section */}
              {!isFreeTier && (() => {
                const comparisonLimit = getComparisonLimits(planName, usageData.addonComparisons);
                const hasReachedLimit = usageData.projectComparisons >= comparisonLimit;
                
                // Always show extra comparisons section for paid users
                  return (
                    <div className={styles.extraComparisonsSection}>
                      <h3>
                        <i className="fas fa-plus-circle"></i>
                        Need More Comparisons?
                      </h3>
                      <p className={styles.limitWarning}>
                        {hasReachedLimit 
                          ? `You've reached your limit of ${comparisonLimit} comparisons for this billing period.`
                        : `You have ${usageData.projectComparisons} of ${comparisonLimit} comparisons used. Get extra comparisons when you need them!`
                        }
                      </p>
                      
                      <div className={styles.addOnOptions}>
                        <div className={styles.addOnCard}>
                          <div className={styles.addOnHeader}>
                            <div className={styles.addOnIcon}>
                              <i className="fas fa-chart-line"></i>
                            </div>
                            <div className={styles.addOnDetails}>
                              <h4>Extra Comparisons Pack</h4>
                              <p>Add 5 additional project comparisons</p>
                            </div>
                          </div>
                          
                          <div className={styles.addOnPricing}>
                            <div className={styles.addOnPrice}>
                              <span className={styles.currency}>$</span>
                            <span className={styles.amount}>{ADDON_CONFIG['extra-comparisons'].price}</span>
                              <span className={styles.period}>one-time</span>
                            </div>
                            <div className={styles.pricePerComparison}>
                            ${ADDON_CONFIG['extra-comparisons'].pricePerUnit.toFixed(2)} per comparison
                            </div>
                          </div>
                          
                          <button 
                            className={styles.buyNowButton}
                            onClick={() => {
                              onClose();
                            // Navigate to checkout with add-on data using correct route
                            navigate('/homeowner/checkout?' + new URLSearchParams({
                                type: 'addon',
                                product: 'extra-comparisons',
                                quantity: '5',
                                planId: subscriptionData?.plan_id || '',
                                planName: planName
                            }).toString());
                            }}
                          >
                            <i className="fas fa-shopping-cart"></i>
                            Buy Now
                          </button>
                        </div>
                        

                      </div>
                    </div>
                  );
              })()}

              {/* Upgrade CTA for free users */}
              {isFreeTier && (
                <div className={styles.upgradeSection}>
                  <h3>Ready to Upgrade?</h3>
                  <p>Get more storage, more comparisons, and premium features.</p>
                  <button 
                    className={styles.upgradeButton}
                    onClick={() => {
                      onClose();
                      window.location.href = '/homeowner/pricing';
                    }}
                  >
                    <i className="fas fa-star"></i>
                    View Plans
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanUsageModal; 